import { LogoutIcon, PopoverPlacement, VerticalDivider } from '@trarc/pc-shared';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProtonLogoSimple } from '../../assets';
import { PATHS } from '../../constants';
import { useSessionContext } from '../../contexts/SessionContext';
import { useAppContext } from '../../hooks';
import { MeQuery, ShadowType, useSystemRemoveShadowEntityMutation } from '../../types/api.graphql';
import { StyledRoleIcon } from '../CommonIcons';
import {
  LogoContainer,
  ShadowEntityContainer,
  ShadowEntityLabel,
  ShadowEntityText,
  SharedHeader,
  StyledChevronIcon,
  StyledPopoverButton,
  UserActionDropdownRow,
  UserInfo,
  UserName,
} from './Header.styles';

export const Header = () => {
  const { me, shadowEntity } = useSessionContext();
  const { logout } = useAppContext();
  const { t } = useTranslation();
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [removeShadowEntity] = useSystemRemoveShadowEntityMutation({
    onCompleted: () => {
      setPopoverOpen(false);
      window.location.reload();
    },
  });

  const handleRemoveShadowEntity = async () => {
    await removeShadowEntity({
      variables: {
        userId: me?.id,
      },
    });
  };

  const getShadowEntityName = (shadowEntity: MeQuery['me']['shadowEntity']['shadowEntity']) => {
    switch (shadowEntity?.__typename) {
      case ShadowType.User: {
        return `${shadowEntity?.lastName}, ${shadowEntity?.firstName}`;
      }
      case ShadowType.Organization: {
        return `${shadowEntity?.name}`;
      }
      default:
        return undefined;
    }
  };

  const shadowEntityName = getShadowEntityName(shadowEntity?.shadowEntity);

  return (
    <SharedHeader
      logo={
        <LogoContainer>
          <ProtonLogoSimple />
          <span>Admin</span>
        </LogoContainer>
      }
      logoPath={PATHS.ROOT}
      links={[
        { to: PATHS.Dealerships.ROOT, label: t('dealerships.link') },
        { to: PATHS.RiskAssessmentDocuments.ROOT, label: t('riskAssessments.link') },
        { to: PATHS.ApplicationUsers.ROOT, label: t('users.link') },
        { to: PATHS.Vendors.ROOT, label: t('vendors.link') },
        { to: PATHS.VendorRequests.ROOT, label: t('vendorRequests.link') },
        { to: PATHS.Documents.ROOT, label: t('documents.link') },
        { to: PATHS.VendorRisks.ROOT, label: t('vendorRisks.link') },
        { to: PATHS.ThirdPartyApi.ROOT, label: t('thirdPartyApi.link') },
        { to: PATHS.AffiliateGroups.ROOT, label: t('affiliateGroup.link') },
        { to: PATHS.Statistics.ROOT, label: t('statistics.link') },
        { to: PATHS.CustomNotifications.ROOT, label: t('customNotifications.link') },
      ]}
      rightContent={
        me && (
          <>
            {!!shadowEntity && !!shadowEntity?.shadowEntity && (
              <ShadowEntityContainer>
                <ShadowEntityLabel>{t('profile.shadowEntityLabel')}</ShadowEntityLabel>
                <ShadowEntityText>{shadowEntityName}</ShadowEntityText>
              </ShadowEntityContainer>
            )}
            <VerticalDivider />
            <StyledPopoverButton
              isOpen={isPopoverOpen}
              onOpenChange={setPopoverOpen}
              placement={PopoverPlacement.BottomRight}
              triggerDisplay={
                <UserInfo>
                  <UserName>{`${me?.firstName} ${me?.lastName}`}</UserName>
                  <StyledChevronIcon />
                </UserInfo>
              }
            >
              <UserActionDropdownRow
                onClick={logout}
                children={
                  <>
                    <LogoutIcon stroke="black" />
                    <span>{t('profile.logout')}</span>
                  </>
                }
              />
              {!!shadowEntity && (
                <UserActionDropdownRow
                  onClick={handleRemoveShadowEntity}
                  children={
                    <>
                      <StyledRoleIcon stroke="black" />
                      <span>{t('profile.stopShadow')}</span>
                    </>
                  }
                />
              )}
            </StyledPopoverButton>
          </>
        )
      }
    />
  );
};
